import { fn } from '@ember/helper';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import type { Registry as Services } from '@ember/service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import {
  PrismButton,
  PrismComboSelectInput,
  PrismIcon,
  PrismLoadingBar,
  PrismModal,
} from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { eq, or } from 'ember-truth-helpers';
import { local } from 'embroider-css-modules';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import GeneralButtonsChoiceSegmentButton from 'lumen/components/general/buttons/choice-segment-button';
import type { BudgetForecastEntry } from 'lumen/models';
import { compareEntries } from 'lumen/models/budget-forecast/entry';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { BudgetForecastDataFetching } from 'lumen/utils/budget-forecast/data-fetching';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

import styles from './modal-copy-to-existing.module.css';

export interface SettingsBudgetForecastModalsModalCopyToExistingSignature {
  Args: {
    entry: BudgetForecastEntry;
    onClose: () => void;
  };
}

export default class SettingsBudgetForecastModalsModalCopyToExisting extends Component<SettingsBudgetForecastModalsModalCopyToExistingSignature> {
  @service declare ajax: AjaxService;
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare toast: Services['toast'];

  @tracked doFormValidation = false;
  @tracked selectedDataType: 'budget' | 'forecast' = 'budget';
  @tracked selectedEntry: BudgetForecastEntry | null = null;

  #dataFetching: BudgetForecastDataFetching;

  budgetEntriesTask = task({ restartable: true }, async () => {
    return this.#dataFetching.fetchEntriesTask.perform('budget');
  });

  copyToExistingTask = task(async (): Promise<void> => {
    this.doFormValidation = true;

    if (!this.selectedEntry) {
      return;
    }

    try {
      const baseUrl = `/apigateway/v1/app/budgetforecast/entries/${this.args.entry.id}/copy`;
      const queryParams = {
        subscription_id: this.hotelAndMarketService.hotel.id!,
      };

      const url = formatUrl(baseUrl, queryParams, this.ajax.host);
      await this.ajax.request(url, {
        method: 'POST',
        data: {
          target_entry_id: parseInt(this.selectedEntry.id!),
        },
      });

      this.#handleCopyToExistingSuccess(this.selectedEntry);
    } catch (error) {
      this.errorTracking.captureException(error);
      void this.alerting.showErrorAlert();
      this.toast.error(this.intl.t('general.error_short'), { showIcon: true });

      throw new SilentTaskError(getErrorMessage(error));
    }
  });

  forecastEntriesTask = task({ restartable: true }, async () => {
    return this.#dataFetching.fetchEntriesTask.perform('forecast');
  });

  @cached // Preserve reference for isEqual check in PowerSelect code
  get entryOptions(): {
    badge: string | undefined;
    entry: BudgetForecastEntry;
    id: string;
    label: string;
  }[] {
    const taskToUse =
      this.selectedDataType === 'budget'
        ? this.budgetEntriesTask
        : this.forecastEntriesTask;

    if (taskToUse.isRunning || !taskToUse.last?.value) {
      return [];
    }

    return taskToUse.last.value
      .filter((entry) => !entry.isLocked && !entry.isArchived && entry.canEdit)
      .filter((entry) => entry.id !== this.args.entry.id)
      .toSorted(compareEntries)
      .map((entry) => ({
        id: entry.id!,
        label: entry.titleWithDefault,
        badge: entry.isMain ? this.intl.t('budget_forecast.main') : undefined,
        entry,
      }));
  }

  get selectedValue(): (typeof this.entryOptions)[number] | undefined {
    return this.entryOptions.find((o) => o.id === this.selectedEntry?.id);
  }

  get shouldShowEntryError(): boolean {
    return this.doFormValidation && !this.selectedEntry;
  }

  constructor(
    owner: Owner,
    args: SettingsBudgetForecastModalsModalCopyToExistingSignature['Args'],
  ) {
    super(owner, args);

    this.#dataFetching = new BudgetForecastDataFetching(owner);
    void this.budgetEntriesTask.perform();
    void this.forecastEntriesTask.perform();
  }

  #handleCopyToExistingSuccess(entry: BudgetForecastEntry): void {
    this.toast.success(
      this.intl.t('budget_forecast.copy_to_existing.success', {
        source: this.args.entry.titleWithDefault,
        target: entry.titleWithDefault,
      }),
      { showIcon: true },
    );

    this.args.onClose();

    const routeToTransitionTo =
      entry.dataType === 'budget'
        ? 'hotel.settings.budget.detail'
        : 'hotel.settings.forecast.detail';

    this.router.transitionTo(routeToTransitionTo, entry.id);
  }

  @action
  setSelectedDataType(dataType: 'budget' | 'forecast'): void {
    this.selectedDataType = dataType;
    this.selectedEntry = null;
    this.doFormValidation = false;
  }

  @action
  setSelectedEntry(option: { entry: BudgetForecastEntry }): void {
    this.selectedEntry = option.entry;
  }

  <template>
    <PrismModal
      @isCloseDisabled={{this.copyToExistingTask.isRunning}}
      @onClose={{@onClose}}
      @title={{if
        (eq @entry.dataType "budget")
        (t "budget_forecast.copy_to_existing.budget")
        (t "budget_forecast.copy_to_existing.forecast")
      }}
      data-test-modal="copy-to-existing"
    >
      <:default>
        <div class="modal--padded modal--small relative flex flex-col gap-500">
          {{#if
            (or
              this.budgetEntriesTask.isRunning
              this.forecastEntriesTask.isRunning
            )
          }}
            <PrismLoadingBar />
          {{/if}}
          <div class="flex gap-500">
            <GeneralButtonsChoiceSegmentButton
              @isChecked={{eq this.selectedDataType "budget"}}
              @isDisabled={{this.copyToExistingTask.isRunning}}
              @label={{t "settings.budget.title"}}
              @onClick={{fn this.setSelectedDataType "budget"}}
              @type="radio"
              data-test-radio="budget"
            />

            <GeneralButtonsChoiceSegmentButton
              @isChecked={{eq this.selectedDataType "forecast"}}
              @isDisabled={{this.copyToExistingTask.isRunning}}
              @label={{t "settings.forecast.title"}}
              @onClick={{fn this.setSelectedDataType "forecast"}}
              @type="radio"
              data-test-radio="forecast"
            />
          </div>

          <div class="grid gap-100">
            <PrismComboSelectInput
              @disabled={{this.copyToExistingTask.isRunning}}
              @isSecondary={{true}}
              @onChange={{this.setSelectedEntry}}
              @options={{this.entryOptions}}
              @placeholder={{if
                (eq this.selectedDataType "budget")
                (t "budget_forecast.copy_to_existing.select_entry.budget")
                (t "budget_forecast.copy_to_existing.select_entry.forecast")
              }}
              @unsafe_matchTriggerWidth={{true}}
              @value={{this.selectedValue}}
              class={{local
                styles
                (if this.shouldShowEntryError "entry-select--with-error")
              }}
              data-test-field="entry-select"
            />
            {{#if this.shouldShowEntryError}}
              <div class="flex items-center gap-100">
                <PrismIcon
                  @color="critical"
                  @name="circle-error-filled"
                  @size="small"
                />
                <p
                  class="text-critical-default text-200--medium"
                  data-test-error="entry-select"
                >
                  {{#if (eq this.selectedDataType "budget")}}
                    {{t "budget_forecast.copy_to_existing.error.budget"}}
                  {{else}}
                    {{t "budget_forecast.copy_to_existing.error.forecast"}}
                  {{/if}}
                </p>
              </div>
            {{/if}}
          </div>

          <p class="paragraph-300--regular" data-test-description>
            {{#if (eq this.selectedDataType "budget")}}
              {{t "budget_forecast.copy_to_existing.description.budget"}}
            {{else}}
              {{t "budget_forecast.copy_to_existing.description.forecast"}}
            {{/if}}
          </p>
        </div>
      </:default>
      <:footer>
        <div class="mx-500 flex h-full items-center justify-end gap-200">
          <PrismButton
            @buttonType="secondary"
            @isDisabled={{this.copyToExistingTask.isRunning}}
            @label={{t "general.cancel"}}
            @onClick={{@onClose}}
            data-test-button="cancel"
          />
          <PrismButton
            @isDisabled={{this.copyToExistingTask.isRunning}}
            @isLoading={{this.copyToExistingTask.isRunning}}
            @label={{if
              (eq @entry.dataType "budget")
              (t "budget_forecast.copy_to_existing.copy.budget")
              (t "budget_forecast.copy_to_existing.copy.forecast")
            }}
            @onClick={{this.copyToExistingTask.perform}}
            data-test-button="copy"
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
